import { createContext, useContext, type ReactNode } from "react";
import type {
  SupplierPortalDataAdapter,
  SupplierPortalSignInAdapter,
} from "./supplier-portal-adapter";
import type { SupplierPicksAdapter } from "./supplier-picks-adapter";
import type { SupplierGoodsInAdapter } from "./supplier-goods-in-adapter";

interface SupplierPortalAdapters {
  /** The purchase-order half of the portal: orders, invoices, account, the order page. */
  data: SupplierPortalDataAdapter;
  signIn: SupplierPortalSignInAdapter;
  picks: SupplierPicksAdapter;
  goodsIn: SupplierGoodsInAdapter;
}

const SupplierPortalAdapterContext =
  createContext<SupplierPortalAdapters | null>(null);

/**
 * The supplier portal's adapters (see `supplier-portal-adapter.ts`, `supplier-picks-adapter.ts` and
 * `supplier-goods-in-adapter.ts`): its reads and writes, the warehouse picks and goods in, and the login half of
 * supplier access. The shell picks real or mock once at boot
 * (`apps/supplier-portal/src/main.tsx`).
 */
export function SupplierPortalAdapterProvider({
  data,
  signIn,
  picks,
  goodsIn,
  children,
}: SupplierPortalAdapters & { children: ReactNode }) {
  return (
    <SupplierPortalAdapterContext.Provider
      value={{ data, signIn, picks, goodsIn }}
    >
      {children}
    </SupplierPortalAdapterContext.Provider>
  );
}

function useAdapters(): SupplierPortalAdapters {
  const adapters = useContext(SupplierPortalAdapterContext);
  if (!adapters) {
    throw new Error(
      "Supplier portal adapters are only available inside a SupplierPortalAdapterProvider",
    );
  }
  return adapters;
}

/** The purchase-order adapter. */
export function useSupplierPortalAdapter(): SupplierPortalDataAdapter {
  return useAdapters().data;
}

export function useSupplierPortalSignIn(): SupplierPortalSignInAdapter {
  return useAdapters().signIn;
}

export function useSupplierPicksAdapter(): SupplierPicksAdapter {
  return useAdapters().picks;
}

export function useSupplierGoodsInAdapter(): SupplierGoodsInAdapter {
  return useAdapters().goodsIn;
}
